/**
 * 페이지 번호 변환 유틸리티
 *
 * ⚠️ 중요: 페이지 번호는 UI 기준 1-based (1, 2, 3, ...)
 * - UI Index: 화면에 보이는 페이지 번호 (1-based)
 * - Document Page: PDF 문서의 실제 페이지 번호 (1-based)
 * - API Index: API 호출 시 사용하는 인덱스 (0-based)
 */

/**
 * 0-based API 인덱스를 1-based UI 페이지로 변환
 */
export const toOneBasedPage = (zeroBasedIndex: number): number => {
	return zeroBasedIndex + 1;
};

/**
 * 1-based UI 페이지를 0-based API 인덱스로 변환
 */
export const toZeroBasedIndex = (oneBasedPage: number): number => {
	return oneBasedPage - 1;
};

/**
 * 문서 페이지 번호를 UI 인덱스로 변환
 * @param documentPage 문서의 실제 페이지 번호 (1-based)
 * @param pages 로드된 페이지 번호 배열 (정렬된 1-based 배열)
 * @returns UI 인덱스 (1-based), 찾지 못하면 0
 */
export const documentPageToUiIndex = (documentPage: number, pages: number[]): number => {
	const index = pages.indexOf(documentPage);
	return index === -1 ? 0 : index + 1;
};

/**
 * UI 인덱스를 문서 페이지 번호로 변환
 * @param uiIndex UI 인덱스 (1-based)
 * @param pages 로드된 페이지 번호 배열 (정렬된 1-based 배열)
 * @returns 문서 페이지 번호 (1-based), 찾지 못하면 undefined
 */
export const uiIndexToDocumentPage = (uiIndex: number, pages: number[]): number | undefined => {
	return pages[uiIndex - 1];
};

/**
 * 페이지 범위 파싱 (예: "1,3-5,7" → [1,3,4,5,7])
 * @param selectPage 페이지 선택 문자열 또는 숫자
 * @returns 정렬된 페이지 번호 배열
 */
export const parsePages = (selectPage: number | string | undefined): number[] => {
	if (!selectPage) return [];
	if (typeof selectPage === 'number') return [selectPage];

	const pages = new Set<number>();
	const ranges = selectPage.split(',');

	ranges.forEach(range => {
		const [start, end] = range.split('-').map(Number);
		if (!end) {
			pages.add(start);
			return;
		}

		Array.from({ length: end - start + 1 }, (_, i) => start + i).forEach(num => pages.add(num));
	});

	return Array.from(pages).sort((a, b) => a - b);
};
